import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { Box, Typography, Accordion as MuiAccordion, AccordionSummary, AccordionDetails } from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'

import Badge from 'components/Badge'
import { pdsAnalytics } from 'analytics/pdsAnalytics'
import AccordionContent from './AccordionContent'
import { MESSAGES } from '../messages/messages'

function Accordion({ summary, details, darkBackground, footer, hideExpandIcon, order = 0, defaultExpanded = false }) {
    const id = summary?.desc?.[0] || ''
    const controlId = id.replaceAll(' ', '-')
    let listKey = Date.now()
    const [expanded, setExpanded] = useState(defaultExpanded)

    const handleChange = () => {
        const eventValues = {
            linkType: 'expand',
            contentSubType: 'pds accordion',
            sateliteTrackId: 'moduleExpandContent'
        }
        if (!expanded) {
            pdsAnalytics.clickEvent(summary.desc[0], eventValues)
        }
        setExpanded(hideExpandIcon ? expanded : !expanded)
    }

    return (
        <MuiAccordion
            disableGutters
            elevation={0}
            square
            expanded={expanded}
            onChange={handleChange}
            className={!hideExpandIcon ? 'Mui-expanded' : ''}
            sx={{
                backgroundColor: darkBackground ? 'background.paper' : 'inherit',
                borderRadius: darkBackground ? '10px' : 'inherit',
                pb: { xs: darkBackground ? 2 : 0, md: darkBackground ? 6 : 0 },
                order: order,
                '&:last-of-type': {
                    '.MuiAccordionDetails-root': {
                        '.MuiBox-root': {
                            '&:last-of-type': {
                                borderBottom: 'none',
                                pb: 0
                            }
                        }
                    }
                },
                '.MuiAccordionDetails-root': {
                    '.MuiBox-root': {
                        mt: 6,
                        pr: { xs: 6, md: 7 },
                        '&:last-of-type': {
                            borderBottom: (theme) => `1px solid ${theme.palette.common.black[5]}`,
                            pb: 6
                        }
                    },
                    '.MuiAccordion-root': {
                        pb: 6,
                        borderBottom: (theme) => `1px solid ${theme.palette.common.black[5]}`,
                        '&.Mui-expanded': {
                            pb: 6,
                            borderBottom: (theme) => `1px solid ${theme.palette.common.black[5]}`
                        },
                        '.MuiAccordionSummary': {
                            '&-root': {
                                borderBottom: 'none'
                            },
                            '&-content': {
                                mb: 0
                            }
                        }
                    },
                    '.MuiAccordionSummary': {
                        '&-root': {
                            mx: 0
                        },
                        '&-content': {
                            '&.Mui-expanded': {
                                mb: 0
                            },
                            '.MuiTypography-boldBody1': {
                                fontSize: '14px',
                                lineHeight: '24px',
                                fontWeight: 600
                            }
                        }
                    },
                    '.MuiAccordionDetails-root': {
                        ml: { xs: 3, md: 6 },
                        borderTop: 'none',
                        '.MuiBox-root': {
                            pr: { xs: 3, md: 1 },
                            '&:last-of-type': {
                                borderBottom: 'none',
                                pb: 0
                            }
                        }
                    }
                }
            }}
        >
            <AccordionSummary
                tabIndex={0}
                expandIcon={
                    <FontAwesomeIcon icon={faAngleDown} style={{ visibility: hideExpandIcon ? 'hidden' : 'unset' }} />
                }
                aria-controls={`${controlId}-panel-content`}
                id={`${id}-panel-header`}
                data-testid={`${id}-panel-header`}
                sx={{
                    '.MuiAccordionSummary-content': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: summary.includeAXZPlan ? 2 : 6,
                        '&.Mui-expanded': {
                            '& .MuiTypography-root:not(:first-of-type)': {
                                display: 'none'
                            }
                        }
                    },
                    ...summary.styles
                }}
            >
                {(summary?.desc || []).map((text, index) => (
                    <Typography
                        variant="boldBody1"
                        tabIndex={0}
                        key={`${index + listKey}`}
                        data-testid={`${id}-panel-header-summary-${index}`}
                        sx={{
                            display: index === 0 ? 'inline-flex' : 'inline',
                            flex: index === 0 ? 1.7 : 1,
                            letterSpacing: 1,
                            textTransform: 'capitalize',
                            textAlign: index === 0 ? 'left' : 'right',
                            textWrap: 'balance'
                        }}
                    >
                        <Typography variant="boldBody1" component="span">
                            {text}
                            {Array.isArray(summary?.badge) && summary?.badge[index] && (
                                <Badge badgeContent={summary.badgeContent[index]}></Badge>
                            )}
                            {summary?.includeAXZPlan && index === 0 && (
                                <Typography
                                    variant="regularCaption"
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: 0
                                    }}
                                >
                                    {MESSAGES.includes_axz_plan_pricing}
                                </Typography>
                            )}
                        </Typography>
                    </Typography>
                ))}
            </AccordionSummary>
            <AccordionDetails>
                {(details || []).map(
                    (
                        {
                            desc,
                            axzPlanPrice = false,
                            axzPrice = [],
                            bold = [],
                            badge = [],
                            badgeContent = [],
                            mandatory = false,
                            ...props
                        },
                        index
                    ) => {
                        return props?.details ? (
                            <Accordion key={`${index + listKey}`} {...props} />
                        ) : (
                            <Box
                                tabIndex={0}
                                key={`${index + listKey}`}
                                data-testid={`${id}-panel-content-details-${index}`}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2
                                }}
                            >
                                {(desc || []).map((text, index) => (
                                    <AccordionContent
                                        text={text}
                                        index={index}
                                        axzPlanPrice={axzPlanPrice}
                                        axzPrice={axzPrice}
                                        badge={badge}
                                        badgeContent={badgeContent}
                                        bold={bold}
                                        mandatory={mandatory}
                                        key={`${index + listKey}`}
                                    />
                                ))}
                            </Box>
                        )
                    }
                )}
            </AccordionDetails>
            {footer?.desc && footer?.desc.length > 0 && (
                <Box
                    data-testid={`${id}-panel-content-footer`}
                    sx={{
                        backgroundColor: 'text.primary',
                        borderRadius: 2.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 6,
                        mx: { xs: 1, md: 3 },
                        pl: { xs: 2, md: 3 },
                        pr: { xs: 8, md: 9.5 }
                    }}
                >
                    {(footer?.desc || []).map((text, index) => (
                        <Typography
                            tabIndex={0}
                            component={'span'}
                            variant="boldBody1"
                            color="primary.contrastText"
                            key={`${index + listKey}`}
                            sx={{
                                flex: index === 0 ? { xs: 1.3, md: 1.5, lg: 1.7 } : 1,
                                letterSpacing: '1px',
                                py: 4,
                                textTransform: 'capitalize',
                                textAlign: index === 0 ? 'left' : 'right',
                                textWrap: 'balance'
                            }}
                        >
                            {text}
                            {footer?.badge?.[index] && <Badge badgeContent={footer?.badgeContent[index]}></Badge>}
                        </Typography>
                    ))}
                </Box>
            )}
        </MuiAccordion>
    )
}

const accordionContentType = {
    desc: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    badge: PropTypes.arrayOf(PropTypes.bool),
    badgeContent: PropTypes.arrayOf(PropTypes.number),
    includeAXZPlan: PropTypes.bool
}

Accordion.propTypes = {
    summary: PropTypes.shape(accordionContentType),
    details: PropTypes.arrayOf(
        PropTypes.shape({
            ...accordionContentType,
            bold: PropTypes.arrayOf(PropTypes.bool)
        })
    ),
    darkBackground: PropTypes.bool,
    footer: PropTypes.shape(accordionContentType),
    hideExpandIcon: PropTypes.bool,
    order: PropTypes.number,
    defaultExpanded: PropTypes.bool
}

export default Accordion
